import {
  type DownloadedFile,
  type ExpenseAttachmentResource,
  ExpenseAttachmentsController_content_v1,
  ExpenseAttachmentsController_list_v1,
  ExpenseAttachmentsController_record_v1,
  type ExpenseResource,
  messageForErrorCode,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@repo/ui/components/dialog';
import {
  IconAlertTriangle,
  IconDownload,
  IconFileTypePdf,
  IconPaperclip,
} from '@tabler/icons-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { isWorthRetrying, messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatInstant } from '@/lib/instant';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { FormDialogContent } from '../auth/AuthFrame';
import { LoadFailed } from '../back-office/Screen';
import { saveDownloadedFile } from './cash-book-export';

/**
 * The receipts and invoices attached to one expense (#513, blossom-rest-api#515).
 *
 * ## Bytes go through the client, never a URL
 *
 * `…/content` authenticates with the session bearer and nothing else, so an
 * `<img src>` or an anchor pointed at it is an unauthenticated request. Every
 * file is fetched with `ApiClient.download` and shown or saved from the blob —
 * the same reason `cash-book-export.ts` gives. The object URLs a thumbnail holds
 * are revoked when it unmounts.
 *
 * ## One key per picked file
 *
 * `attemptFor` keyed on the pick, as `MachineUpload` does: sending the same pick
 * again after a dropped answer replays, and the next pick is a new write. The
 * server also recognises the same bytes on the same expense and answers `200`
 * with the attachment already there — which is what `onStatus` is read for.
 *
 * ## Against an API older than #513
 *
 * `Expenses` offers this dialog only where the expense carries
 * `attachmentCount`, so it is never opened against a server that lacks the
 * routes.
 */

/** 5 MiB, the upload route's bound. Checked here so a slow line is not spent on a refusal. */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** The four types the API sniffs for. It decides from the bytes; this only steers the picker. */
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly items: readonly ExpenseAttachmentResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

type UploadState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending'; readonly fileName: string }
  | { readonly kind: 'done'; readonly fileName: string; readonly already: boolean }
  | { readonly kind: 'refused'; readonly message: string; readonly retry: File | null };

export function ExpenseAttachmentsDialog({
  expense,
  open,
  onOpenChange,
  onAttached,
}: {
  expense: ExpenseResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A new attachment landed, so the list's count is stale. */
  onAttached: () => void;
}) {
  const client = useApiClient();
  const inputId = useId();

  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [upload, setUpload] = useState<UploadState>({ kind: 'idle' });

  const picks = useRef(0);
  const attempt = useRef<WriteAttempt | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    let live = true;

    setList({ kind: 'loading' });

    client
      .request(ExpenseAttachmentsController_list_v1, {
        path: { expenseId: expense.id },
        signal: controller.signal,
      })
      .then((items) => {
        if (live) setList({ kind: 'loaded', items });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setList({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, expense.id, open, reloads]);

  const send = useCallback(
    async (file: File) => {
      const sending = attemptFor(attempt.current, `pick-${String(picks.current)}`);
      attempt.current = sending;

      setUpload({ kind: 'sending', fileName: file.name });

      let status = 0;
      try {
        await client.request(ExpenseAttachmentsController_record_v1, {
          path: { expenseId: expense.id },
          query: { fileName: file.name },
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
          // The File itself: its bytes, never `File.text()`.
          body: file,
          onStatus: (answered) => {
            status = answered;
          },
        });
        attempt.current = null;
        setUpload({ kind: 'done', fileName: file.name, already: status === 200 });
        setReloads((count) => count + 1);
        if (status !== 200) onAttached();
      } catch (error: unknown) {
        setUpload({
          kind: 'refused',
          message: messageForFailure(error),
          retry: isWorthRetrying(error) ? file : null,
        });
      }
    },
    [client, expense.id, onAttached],
  );

  const pick = (file: File) => {
    picks.current += 1;
    attempt.current = null;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      setUpload({
        kind: 'refused',
        message: messageForErrorCode('VALIDATION_PAYLOAD_TOO_LARGE'),
        retry: null,
      });
      return;
    }

    void send(file);
  };

  const items = list.kind === 'loaded' ? list.items : [];
  const full = list.kind === 'loaded' && items.length >= 5;
  const sending = upload.kind === 'sending';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Receipts and invoices</DialogTitle>
          <DialogDescription>
            A photo or PDF of what was paid for — up to 5 on one expense, each up to 5 MB.
            Attachments cannot be removed: a wrong one stays, and the right one is added beside it.
            Attaching still works after the day is closed, because it changes no figure.
          </DialogDescription>
        </DialogHeader>

        {list.kind === 'loading' && (
          <p role="status" className="text-sm text-muted-foreground">
            Reading the attachments…
          </p>
        )}

        {list.kind === 'failed' && (
          <div className="space-y-2 text-center">
            <LoadFailed
              error={list.error}
              title="The attachments could not be read"
              onRetry={() => setReloads((count) => count + 1)}
            />
          </div>
        )}

        {list.kind === 'loaded' &&
          (items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing is attached to this expense yet.
            </p>
          ) : (
            <ul className="grid gap-3">
              {items.map((attachment) => (
                <AttachmentItem key={attachment.id} attachment={attachment} />
              ))}
            </ul>
          ))}

        <div className="space-y-2">
          <label
            htmlFor={inputId}
            className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase"
          >
            Attach a photo or PDF
          </label>
          {/* `capture` opens the camera on a phone and is ignored on a desk. */}
          <input
            id={inputId}
            type="file"
            accept={ACCEPT}
            capture="environment"
            disabled={sending || full || list.kind !== 'loaded'}
            className="w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:text-foreground disabled:opacity-60"
            onChange={(event) => {
              const chosen = event.target.files?.[0];
              // A browser fires no `change` for the same file chosen twice.
              event.target.value = '';
              if (chosen !== undefined) pick(chosen);
            }}
          />
          {full && (
            <p className="text-sm text-muted-foreground">
              This expense has 5 attachments, the most it can have.
            </p>
          )}
        </div>

        {upload.kind === 'sending' && (
          <p role="status" className="flex items-center gap-2 text-sm text-foreground">
            <IconPaperclip className="size-4" aria-hidden />
            Attaching <span className="break-all">{upload.fileName}</span>…
          </p>
        )}

        {upload.kind === 'done' && (
          <p role="status" className="text-sm text-foreground">
            {upload.already ? (
              <>
                <span className="break-all">{upload.fileName}</span> was already attached to this
                expense. Nothing new was added.
              </>
            ) : (
              <>
                <span className="break-all">{upload.fileName}</span> is attached.
              </>
            )}
          </p>
        )}

        {upload.kind === 'refused' && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>
              {upload.message}
              {/* Same pick, same key: a retry replays rather than attaching twice. */}
              {upload.retry !== null && (
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (upload.retry !== null) void send(upload.retry);
                    }}
                  >
                    Try again
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
      </FormDialogContent>
    </Dialog>
  );
}

/**
 * One attachment: a thumbnail for an image, fetched on the authenticated
 * transport and shown from a blob URL; a PDF icon otherwise. Both download
 * through the same transport.
 */
function AttachmentItem({ attachment }: { attachment: ExpenseAttachmentResource }) {
  const client = useApiClient();
  const isImage = attachment.contentType !== 'application/pdf';

  const [file, setFile] = useState<DownloadedFile | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const fetchFile = useCallback(
    (signal?: AbortSignal) =>
      client.download(ExpenseAttachmentsController_content_v1, {
        path: { expenseId: attachment.expenseId, attachmentId: attachment.id },
        signal,
      }),
    [attachment.expenseId, attachment.id, client],
  );

  useEffect(() => {
    if (!isImage) return;

    const controller = new AbortController();
    let url: string | null = null;

    fetchFile(controller.signal)
      .then((fetched) => {
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(fetched.blob);
        setFile(fetched);
        setThumbnail(url);
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) setError(caught);
      });

    return () => {
      controller.abort();
      // A thumbnail's URL pins its blob until revoked.
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [fetchFile, isImage]);

  const download = async () => {
    setSaving(true);
    setError(null);
    try {
      const fetched = file ?? (await fetchFile());
      saveDownloadedFile(fetched, attachment.fileName);
    } catch (caught: unknown) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="flex items-center gap-3 rounded-2xl border border-border bg-card p-2">
      <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted">
        {thumbnail !== null ? (
          <img src={thumbnail} alt="" className="size-full object-cover" />
        ) : (
          !isImage && <IconFileTypePdf className="size-7 text-muted-foreground" aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1 text-sm">
        {/* The uploader's name for it, rendered as data. */}
        <p className="break-all text-foreground">{attachment.fileName}</p>
        <p className="text-xs text-muted-foreground">
          <span className="numeric">{formatSize(attachment.sizeBytes)}</span> · attached{' '}
          {formatInstant(attachment.recordedAt)}
        </p>
        {error !== null && <p className="text-xs text-destructive">{messageForFailure(error)}</p>}
      </div>

      <Button
        variant="outline"
        size="sm"
        disabled={saving}
        onClick={() => void download()}
        aria-label={`Download ${attachment.fileName}`}
      >
        <IconDownload data-icon="inline-start" />
        Download
      </Button>
    </li>
  );
}

function formatSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('en-IN')} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
